import React, { useEffect, useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Image,
  TouchableOpacity,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { router, Href, useFocusEffect } from "expo-router";
import i18n from "../../locales/i18n";
import { useStock } from "../../hooks/useStock";
import WebSidebar from "./WebSidebar";
import customAxios from "../../utils/customAxios";
import { appActions, selectIsRTL } from "../../store/slices/appSlice";

// Recharts for web charts with proper hover support
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Import icons as PNGs for Web compatibility
// We converted SVGs to PNGs to ensure they render correctly on web
import StockImage from "../../assets/images/stock.png";
import SellImage from "../../assets/images/sell.png";
import DeptImage from "../../assets/images/dept.png";
import StatsImage from "../../assets/images/stats.png";
import SettingsImage from "../../assets/images/settings.png";
import OrdersImage from "../../assets/images/orders.png";

interface TodayStats {
  todayRevenue: number;
  todayProfit: number;
  billCount: number;
}

interface FeatureCard {
  id: string;
  title: string;
  imageSource?: any;
  icon?: string; // Emoji fallback when no image
  url: string;
}

const WebStartScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { sellWithScanner, locale } = useSelector(
    (state: RootState) => state.app,
  );
  const { fetchProducts } = useStock();

  // Use Redux selector for RTL - single source of truth
  const isRTL = useSelector(selectIsRTL);

  const [todayStats, setTodayStats] = useState<TodayStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [chartData, setChartData] = useState<
    { name: string; revenue: number; profit: number }[]
  >([]);

  // Sync Redux locale with i18n.locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      // Ensure Redux state matches i18n.locale
      if (i18n.locale && i18n.locale !== locale) {
        dispatch(appActions.setLocale(i18n.locale));
      }
    }, [dispatch, locale]),
  );

  // Inject CSS for hover effects (only runs once on web)
  useEffect(() => {
    if (typeof document !== "undefined") {
      const styleId = "web-start-screen-styles";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .feature-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease !important;
            cursor: pointer;
            border: 2px solid transparent !important;
          }
          .feature-card:hover {
            transform: translateY(-4px) !important;
            box-shadow: 0 12px 24px rgba(5, 150, 208, 0.12) !important;
            border-color: #0596D0 !important;
          }
          .feature-card:hover .feature-icon {
            transform: scale(1.05) !important;
          }
          .feature-card:hover .feature-title {
            color: #0596D0 !important;
          }
          .feature-icon {
            transition: transform 0.2s ease !important;
          }
          .feature-title {
            transition: color 0.2s ease !important;
          }
          .sale-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease !important;
            cursor: pointer;
            border: 2px solid transparent !important;
          }
          .sale-card:hover {
            transform: translateY(-4px) !important;
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
            border-color: #0596D0 !important;
          }
          .sale-card:hover .sale-icon {
            transform: scale(1.05) !important;
          }
          .sale-icon {
            transition: transform 0.2s ease !important;
          }
          .summary-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease !important;
            cursor: pointer;
            border: 2px solid transparent !important;
          }
          .summary-card:hover {
            transform: translateY(-3px) !important;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
            border-color: #0596D0 !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, []);

  useEffect(() => {
    const fetchTodayStats = async () => {
      try {
        setStatsLoading(true);
        const now = new Date();
        const fmt = (d: Date) =>
          `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;

        const todayStr = fmt(now);
        const monthStart = fmt(new Date(now.getFullYear(), now.getMonth(), 1));

        const [monthRes, todayRes] = await Promise.all([
          customAxios
            .get("/statistic_intervalle", {
              params: { start: monthStart, end: todayStr },
            })
            .catch(() => ({
              data: { factures: [], total: 0, totalprofit: 0 },
            })),
          customAxios
            .get("/statistic_intervalle", {
              params: { start: todayStr, end: todayStr },
            })
            .catch(() => ({
              data: { factures: [], total: 0, totalprofit: 0, totalselles: 0 },
            })),
        ]);

        // Today stats
        setTodayStats({
          todayRevenue: todayRes.data?.total || 0,
          todayProfit: todayRes.data?.totalprofit || 0,
          billCount: todayRes.data?.totalselles || 0,
        });

        // Build chart from this month's factures grouped by day
        const factures = monthRes.data?.factures || [];
        const grouped: Record<string, { revenue: number; profit: number }> = {};
        factures.forEach((f: any) => {
          const d = new Date(f.created_at);
          const key = `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
          if (!grouped[key]) grouped[key] = { revenue: 0, profit: 0 };
          grouped[key].revenue += f.total || 0;
          grouped[key].profit += f.profit || 0;
        });
        const sorted = Object.entries(grouped)
          .sort(([a], [b]) => {
            const [da, ma] = a.split("/").map(Number);
            const [db, mb] = b.split("/").map(Number);
            return ma !== mb ? ma - mb : da - db;
          })
          .slice(-10)
          .map(([name, data]) => ({ name, ...data }));
        setChartData(
          sorted.length > 0
            ? sorted
            : [
                {
                  name: now.getDate().toString().padStart(2, "0"),
                  revenue: 0,
                  profit: 0,
                },
              ],
        );
      } catch (error) {
        setTodayStats({ todayRevenue: 0, todayProfit: 0, billCount: 0 });
        setChartData([{ name: "--", revenue: 0, profit: 0 }]);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchTodayStats();
    fetchProducts(true);
  }, []);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("ar-DZ");
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Feature cards configuration - using PNG images
  const featureCards: FeatureCard[] = [
    {
      id: "bills",
      title: i18n.t("bills"),
      imageSource: StatsImage,
      url: "bills",
    },
    {
      id: "settings",
      title: i18n.t("settings"),
      imageSource: SettingsImage,
      url: "params",
    },
    {
      id: "stock",
      title: i18n.t("stock"),
      imageSource: StockImage,
      url: "stock",
    },
    {
      id: "credit",
      title: i18n.t("credit"),
      imageSource: DeptImage,
      url: "dettes",
    },
    {
      id: "orders",
      title: i18n.t("orders"),
      imageSource: OrdersImage,
      url: "orders",
    },
  ];

  // RTL direction is now handled by WebSidebar component

  // Use native div for web to ensure flexDirection works properly
  // Key forces re-render when locale changes
  return (
    <div
      key={`main-container-${locale}`}
      className="flex flex-1 bg-white h-screen max-h-screen overflow-hidden flex-row"
    >
      {/* Sidebar */}
      <WebSidebar />

      {/* Main Content */}
      <div className="flex-1 overflow-hidden bg-[#F5F5F5] flex flex-col">
        <div className="flex-1 w-full max-w-7xl mx-auto p-6 flex flex-col">
          {/* Top Section - New Sale & Weekly Summary */}
          <div className="flex flex-row gap-6 mb-6 flex-[2] min-h-[180px]">
            {/* New Sale Card */}
            <TouchableOpacity
              style={styles.newSaleCard}
              onPress={() =>
                router.push(
                  (sellWithScanner ? "scanner" : "scannerwithout") as Href,
                )
              }
              activeOpacity={0.9}
              // @ts-ignore - web only className
              className="sale-card"
            >
              <View style={styles.newSaleContent}>
                <Image
                  source={SellImage}
                  style={{ width: 140, height: 140 }}
                  resizeMode="contain"
                  // @ts-ignore - web only className
                  className="sale-icon"
                />
              </View>
              {/* @ts-ignore - web only className */}
              <Text style={styles.newSaleTitle} className="sale-title">
                {i18n.t("sell")}
              </Text>
            </TouchableOpacity>

            {/* Weekly Summary Card - Clickable to go to dashboard */}
            <TouchableOpacity
              style={styles.weeklySummaryCard}
              onPress={() => router.push("/(drawer)/statistics" as Href)}
              activeOpacity={0.9}
              // @ts-ignore - web only className
              className="summary-card"
            >
              <Text style={styles.summaryTitle}>
                {i18n.t("todaySummary") || i18n.t("today")}
              </Text>

              {statsLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#0596D0" />
                </View>
              ) : (
                <View style={styles.statsContent}>
                  <View style={styles.chartAndStatsRow}>
                    {/* Left side - Revenue info */}
                    <View style={styles.revenueSection}>
                      <View style={styles.revenueContainer}>
                        <Text style={styles.currency}>
                          {i18n.t("currency")}
                        </Text>
                        <Text style={styles.revenueAmount}>
                          {formatCurrency(todayStats?.todayRevenue || 0)}
                        </Text>
                      </View>

                      <View
                        style={{ flexDirection: "row", gap: 16, marginTop: 8 }}
                      >
                        <View>
                          <Text
                            style={{
                              fontSize: 12,
                              color: "#999",
                              marginBottom: 2,
                            }}
                          >
                            {i18n.t("profit")}
                          </Text>
                          <Text
                            style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: "#2E7D32",
                            }}
                          >
                            {formatCurrency(todayStats?.todayProfit || 0)}{" "}
                            {i18n.t("currency")}
                          </Text>
                        </View>
                        <View>
                          <Text
                            style={{
                              fontSize: 12,
                              color: "#999",
                              marginBottom: 2,
                            }}
                          >
                            {i18n.t("salesCount")}
                          </Text>
                          <Text
                            style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: "#6A1B9A",
                            }}
                          >
                            {todayStats?.billCount || 0}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Right side - Bar Chart for this month */}
                    <View
                      style={styles.chartSection}
                      onStartShouldSetResponder={() => true}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      <div style={{ width: 380, height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={chartData}
                            margin={{
                              top: 10,
                              right: 20,
                              left: 10,
                              bottom: 10,
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#E5E7EB"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="name"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#9CA3AF", fontSize: 11 }}
                            />
                            <YAxis
                              hide
                              domain={
                                chartData.every(
                                  (d) => d.revenue === 0 && d.profit === 0,
                                )
                                  ? [0, 100]
                                  : ["auto", "auto"]
                              }
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                border: "none",
                                borderRadius: 12,
                                padding: "12px 16px",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                              }}
                              formatter={(value: any, name: any) => [
                                `${Number(value).toLocaleString()} ${i18n.t("currency")}`,
                                name === "revenue"
                                  ? i18n.t("revenue")
                                  : i18n.t("profit"),
                              ]}
                              cursor={{
                                stroke: "#0596D0",
                                strokeWidth: 1,
                                strokeDasharray: "5 5",
                              }}
                            />
                            <Bar
                              dataKey="revenue"
                              fill="#6D7BDB"
                              radius={[4, 4, 0, 0]}
                              barSize={14}
                            />
                            <Bar
                              dataKey="profit"
                              fill="#91CB5E"
                              radius={[4, 4, 0, 0]}
                              barSize={14}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Legend */}
                      <View style={styles.chartLegend}>
                        <View style={styles.legendItem}>
                          <View
                            style={[
                              styles.legendDot,
                              { backgroundColor: "#6D7BDB" },
                            ]}
                          />
                          <Text style={styles.legendText}>
                            {i18n.t("revenue")}
                          </Text>
                        </View>
                        <View style={styles.legendItem}>
                          <View
                            style={[
                              styles.legendDot,
                              { backgroundColor: "#91CB5E" },
                            ]}
                          />
                          <Text style={styles.legendText}>
                            {i18n.t("profit")}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  <View style={styles.dateContainer}>
                    <Text style={styles.dateRange}>{getTodayFormatted()}</Text>
                  </View>
                </View>
              )}
            </TouchableOpacity>
          </div>

          {/* Feature Cards Grid */}
          <div className="flex flex-1 flex-row gap-6 max-h-[360px]">
            {featureCards.map((card) => (
              <TouchableOpacity
                key={card.id}
                style={styles.featureCard}
                onPress={() => router.push(card.url as Href)}
                activeOpacity={0.8}
                // @ts-ignore - web only className
                className="feature-card flex-1"
              >
                <View style={styles.featureIconContainer}>
                  {card.imageSource ? (
                    <Image
                      source={card.imageSource}
                      style={{ width: 120, height: 120 }}
                      resizeMode="contain"
                      // @ts-ignore - web only className
                      className="feature-icon"
                    />
                  ) : (
                    <Text style={{ fontSize: 80 }} className="feature-icon">
                      {card.icon}
                    </Text>
                  )}
                </View>
                {/* @ts-ignore - web only className */}
                <Text style={styles.featureTitle} className="feature-title">
                  {card.title}
                </Text>
              </TouchableOpacity>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    height: "100dvh" as any,
    maxHeight: "100dvh" as any,
    overflow: "hidden",
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    height: "100%",
    overflow: "hidden",
  },
  scrollView: {
    flex: 1,
    height: "100%",
  },
  pageContainer: {
    padding: 24,
    gap: 20,
    height: "100%",
    display: "flex" as any,
    flexDirection: "column",
  },
  topSection: {
    flexDirection: "row",
    gap: 20,
  },
  newSaleCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    minWidth: 220,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  newSaleContent: {
    marginBottom: 12,
  },
  newSaleTitle: {
    color: "#1a1a2e",
    fontSize: 26,
    fontWeight: "700",
  },
  weeklySummaryCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 28,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    overflow: "hidden",
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#1a1a2e",
    textAlign: "right",
    marginBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 40,
  },
  statsContent: {
    flex: 1,
  },
  chartAndStatsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
  },
  revenueSection: {
    flex: 0.4,
    alignItems: "flex-start",
  },
  chartSection: {
    flex: 0.6,
    alignItems: "center",
    overflow: "hidden",
  },
  chartLegend: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 20,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 12,
    color: "#666",
  },
  revenueRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 8,
  },
  revenueContainer: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
  },
  currency: {
    fontSize: 22,
    fontWeight: "600",
    color: "#0596D0",
  },
  revenueAmount: {
    fontSize: 48,
    fontWeight: "700",
    color: "#1a1a2e",
  },
  percentageChange: {
    fontSize: 16,
    color: "#10B981",
    textAlign: "left",
    marginBottom: 16,
  },
  dateContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: "auto",
  },
  dateRange: {
    fontSize: 15,
    color: "#6B7280",
  },
  cardsGrid: {
    flexDirection: "row",
    gap: 16,
    flex: 1,
  },
  featureCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  featureIconContainer: {
    width: 120,
    height: 120,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: "#1a1a2e",
    textAlign: "center",
  },
});

export default WebStartScreen;
